// @givmo/board — the app's shared display formatting.
//
// These four helpers were written independently in five feature decode modules — byte-identical in
// every copy — and this slice would have added three more. They are hoisted here for the reason
// `components/page-shell.tsx` gives for being one component rather than a per-feature copy: the board
// surfaces must not drift apart from each other, and a date that renders one way on the minute book
// and another way on the roster is a bug a reader notices before an author does.
//
// Two properties every one of them keeps, because the surfaces that call them are reading a legal
// record rather than a dashboard:
//
//   * NOTHING THROWS. A malformed or absent timestamp renders as an em dash, never as a crashed row.
//     The record it belongs to is still the organization's record and still has to be readable.
//   * NOTHING IS INVENTED. An absent value renders as absent — there is no "today", no "recently",
//     and no fallback that would put a date in the organization's mouth.

/** A bare calendar date on the wire — `occurred_on`, `served_from`, `term_end_on`, `consent_date`. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Format a date for reading.
 *
 * ⚠ A BARE `YYYY-MM-DD` IS A CALENDAR DATE, NOT AN INSTANT, and it must not be moved by a time zone.
 * `new Date('2015-05-12')` is parsed as UTC MIDNIGHT, so every reader west of UTC saw the day before:
 * a meeting held on 12 May rendered as 11 May, a term that began on 1 January rendered as 31
 * December, and a version adopted on a board's own recorded date rendered as the day before it. In a
 * minute book that is not a cosmetic slip — it contradicts the date the organization recorded, on the very
 * screen that exists to show what was recorded.
 *
 * So a date-only value is built from its own parts in the reader's calendar and never round-trips
 * through UTC. A full timestamp still renders in the reader's zone, which is correct for one: an
 * instant genuinely happened at a moment, and the reader's zone is how they experience it.
 *
 * Tolerant of null/invalid values → em dash.
 */
export function displayDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = parseDisplayDate(iso);
  if (date === null) return '—';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * A date written out in full, "September 24, 2026" — for a date inside a sentence, where the short
 * month reads as an abbreviation (CBG-RC9). The same calendar-date rule as {@link displayDate}: a
 * bare `YYYY-MM-DD` is built from its own parts and never moved by a time zone. `null` for an absent
 * or unreadable value, so a sentence can choose its own words instead of printing a dash.
 */
export function displayLongDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = parseDisplayDate(iso);
  if (date === null) return null;
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * A local `Date` for a date-only value, or the ordinary parse for anything else. `null` when neither
 * yields a real date.
 *
 * ⚠ The parts are RANGE-CHECKED by round-tripping them back out of the constructed date, because
 * `new Date(2026, 12, 45)` does not fail — it rolls over into February 2027. A malformed value has to
 * render as absent rather than as a confident wrong date in a minute book.
 */
function parseDisplayDate(iso: string): Date | null {
  const parts = DATE_ONLY.exec(iso);
  if (parts === null) {
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const year = Number(parts[1]);
  const month = Number(parts[2]);
  const day = Number(parts[3]);
  const date = new Date(year, month - 1, day);
  const rolledOver =
    date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day;
  return rolledOver ? null : date;
}

/**
 * The calendar day a close's record is dated by: the UTC day of the close's instant, as `YYYY-MM-DD`
 * (for {@link displayDate}). A year's annual disclosures and a self-assessment's results are each
 * written by their close, with `occurred_on` the close's UTC day (the backend's `now.date()`), and
 * the results and the record's own body say that day. A list of closes prints it too, so it never
 * disagrees with the record it opens: a board in Los Angeles that closes at 7:43 PM reads the next
 * day everywhere, rather than one day in the list and the next a click later. The board's own day
 * for all of these is CBG-FIX55's.
 *
 * ⚠ The UTC slice is deliberate here, the record's own day, unlike {@link todayAsDateInput}'s
 * default for a date the person is about to choose. `null` for an absent or unreadable instant.
 */
export function closeRecordDay(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
}

/** Format an ISO timestamp as a date and time — the grain an approval or an entry is recorded at. */
export function displayInstant(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * An instant's time of day in the reader's own zone — "10:15 AM" — for the signing window's chip and
 * the line beside a Sign button. The space before AM or PM is a non-breaking one, so a phone never
 * strands the AM on a line of its own (the approved frames keep them together).
 */
export function displayClockTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date
    .toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    .replace(/\s/g, '\u00a0');
}

/**
 * Today as a `YYYY-MM-DD` string in the reader's own zone — the default a new dated entry bears.
 *
 * ⚠ Built from LOCAL CALENDAR PARTS, never from an ISO slice. `new Date().toISOString().slice(0,10)`
 * is the UTC day, which for any reader west of UTC is tomorrow's date all evening — so a secretary
 * recording a Tuesday-evening meeting would be handed Wednesday as the default and would have to
 * notice and correct it. This is the write-side twin of `displayDate`'s read-side rule: the two ends
 * of a bare calendar date have to agree about which day it is, or the date the organization picks and the
 * date it is later shown differ by one.
 */
export function todayAsDateInput(nowMs: number = Date.now()): string {
  const date = new Date(nowMs);
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Turn an unrecognized wire token into something readable rather than dropping it.
 *
 * Every label helper in this app ends here on its default arm, deliberately: a value this build does
 * not know about is a value a later backend added, and showing `executive_review` beats showing
 * nothing at all to somebody trying to read their own minute book.
 */
export function humanize(value: string | null | undefined): string {
  const raw = (value ?? '').trim();
  if (raw.length === 0) return '—';
  const spaced = raw.replace(/[_-]+/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
